import {useUserAvatarPlaceholder} from "@/hooks/useUserAvatarPlaceholder";
import clsxm from "@/lib/clsxm";
import {type FC} from "react";
import {useMeasure} from "react-use";

const OverlappingAvatars: FC<{
  avatarClassName?: string;
  containerClassName?: string;
  isStackReversed?: boolean;
  keyPrefix?: string;
  overlap?: number;
  overflowCountClassName?: string;
  padTo?: number;
  pictures: (string | null | undefined)[];
  size?: number;
  totalCount?: number;
}> = ({
  avatarClassName,
  containerClassName,
  isStackReversed = false,
  keyPrefix = "avatar",
  overlap = 8,
  overflowCountClassName,
  padTo,
  pictures,
  size = 56,
  totalCount,
}) => {
  const UserAvatarPlaceholder = useUserAvatarPlaceholder();
  const [containerRef, {width: containerWidth}] = useMeasure<HTMLDivElement>();

  const isFitMode = totalCount !== undefined;
  const maxItemsFitting = Math.max(1, Math.floor((containerWidth - overlap) / (size - overlap)));
  const visibleCount = isFitMode
    ? totalCount > maxItemsFitting
      ? maxItemsFitting - 1
      : Math.min(maxItemsFitting, totalCount)
    : 0;

  const list = isFitMode
    ? pictures.slice(0, visibleCount)
    : padTo
      ? [...pictures.slice(0, padTo), ...Array.from({length: Math.max(0, padTo - pictures.length)}, () => null)]
      : pictures;
  const overflowCount = isFitMode ? totalCount - list.length : 0;

  const renderAvatars = () => (
    <>
      {list.map((picture, index) => (
        <img
          key={`${keyPrefix}-${index}`}
          src={picture ?? UserAvatarPlaceholder}
          alt=""
          className={clsxm(
            "dark:border-neutral-grey-light-dark shrink-0 rounded-full border border-white object-cover",
            avatarClassName,
          )}
          style={{
            width: size,
            height: size,
            marginLeft: index === 0 ? 0 : -overlap,
            zIndex: isStackReversed ? index : list.length - index,
          }}
        />
      ))}
      {overflowCount > 0 && (
        <div
          className={clsxm(
            "bg-neutral-grey-lightest dark:border-neutral-grey-light-dark flex shrink-0 items-center justify-center rounded-full border border-white px-1",
            avatarClassName,
            overflowCountClassName,
          )}
          style={{
            minWidth: size,
            height: size,
            marginLeft: list.length > 0 ? -overlap : 0,
            zIndex: isStackReversed ? list.length : 0,
          }}>
          <span className={clsxm("text-primary-dark-solid font-semibold", size < 32 ? "text-2xs" : "text-sm")}>
            +{overflowCount}
          </span>
        </div>
      )}
    </>
  );

  if (!isFitMode) return renderAvatars();

  return (
    <div ref={containerRef} className={clsxm("flex min-w-0 flex-1 overflow-hidden", containerClassName)}>
      {renderAvatars()}
    </div>
  );
};

export default OverlappingAvatars;
